import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Container,
  DialogContent,
  Grid,
  InputLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState, useContext } from 'react';
import { useForm } from 'react-hook-form';
import { GaurdianRelation } from 'utils/dropDownList';
import { secondaryGuardianSchema } from 'utils/schema';
import { AuthContext } from 'utils/AuthContext';
import { useAppSelector } from 'store/hooks';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import { toast } from 'react-toastify';
import ReactPhoneInput from 'react-phone-input-material-ui';
import { StyledDialog } from 'theme/index';

const SecondaryGaurdian = (props: any) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(secondaryGuardianSchema),
    mode: 'onChange',
  });
  const guardians = useAppSelector((state) => state.user.guardians);
  const isProfileDetailsFetched = useAppSelector(
    (state) => state.user.loaders.isProfileDetailsFetched
  );
  const { user } = useContext(AuthContext);
  const isAdmin = user.isAdmin;
  const [isMobileNumberRequired, setIsMobileNumberRequired] = useState(false);
  const [disableField, setDisableField] = useState(false);
  const [disableDeleteField, setDisableDeleteField] = useState(true);
  const [readOnlyWarningDialog, setReadOnlyWarningDialog] = useState<boolean>(false);
  const [formData, setFormData] = useState<any>();

  const setUserPermission = (id: BigInteger) => {
    if (isAdmin && user?.permissions?.includes('GLOBAL_EDIT_PROFILE')) {
      setDisableField(false);
      if (id !== undefined) {
        setDisableDeleteField(false);
      }
    } else if (
      (isAdmin && user?.permissions?.includes('EDIT_PROFILE') && id !== undefined) ||
      (!isAdmin && user?.permissions?.includes('EDIT_PROFILE') && id !== undefined)
    ) {
      setDisableField(true);
      if (id !== undefined) {
        setDisableDeleteField(false);
      }
    }
  };

  const setSecondaryGuardianDetails = () => {
    if (props?.secondaryGaurdian) {
      Object.keys(props.secondaryGaurdian).forEach((key: any) => {

        if (key === 'mobileNumber') {
          const str = props.secondaryGaurdian.mobileNumber.replaceAll(' ', '');
          const mobile = str.replace(/[^\w\s]/g, '');
          const phoneNumbers = parseInt(mobile);
          setValue('mobileNumber', `1${phoneNumbers}`, {
            shouldValidate: true,
            shouldDirty: false,
          });
          return;
        }
        setValue(key, props.secondaryGaurdian[key], {
          shouldValidate: true,
          shouldDirty: false,
        });
      });
    } else {
      setValue('other', '', {
        shouldValidate: true,
        shouldDirty: false,
      });
      setValue('id', 0, {
        shouldValidate: true,
        shouldDirty: false,
      });
    }
  };

  useEffect(() => {
    setSecondaryGuardianDetails();
    setUserPermission(props?.secondaryGaurdian?.id);
    //eslint-disable-next-line
  }, [guardians]);

  function formatPhoneNumber(phoneNumberString: any) {
    const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
    const match = cleaned.match(/^(1)?(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      const intlCode = match[1] ? '+1 ' : '';
      return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
    }
    return null;
  }
  //submit SG form
  const onSubmitForm = async (formData: any) => {
    if (
      formData.mobileNumber === user?.mobileNumber ||
      formData.email.toLowerCase() === user?.email.toLowerCase()
    ) {
      toast('Please provide different details than primary guardian', {
        type: 'error',
        icon: true,
      });
      return;
    }

    if (isValid && isDirty) {
      if (formData?.id === 0) {
        setReadOnlyWarningDialog(true);
        setFormData(formData);
        return;
      }
      const payload = guardians
        .filter((guardian: any) => guardian.guardianCategory !== formData.guardianCategory)
        .concat({
          ...formData,
          id: formData?.id ? formData.id : null,
          guardianCategory: 'secondary',
          other: formData.relation === 'other' ? formData?.other : '',
          mobileNumber: formData?.mobileNumber
            ? formatPhoneNumber(formData?.mobileNumber.slice(1))
            : '',
          email: formData?.email ? formData?.email.toLowerCase() : '',
        });
      props.onUpdateGuardian(payload, () => resetData(formData));
    } else {
      toast('No changes to update.', {
        type: 'info',
        icon: true,
      });
    }
  };

  const handleSkipStep = () => {
    props.skipStep();
  };

  const deleteGuardian = () => {
    props.handleDeleteGuardian();
  };

  const resetData = (formData: any) => {
    const message1 = 'Secondary guardian updated successfully.';
    const message2 = 'Secondary guardian updated successfully. You can upload your picture.';
    toast(props.pictureAvailable ? message1 : message2, {
      type: 'success',
      icon: true,
    });
    reset(formData);
  };

  const saveSecondaryGuardian = async (e: any) => {
    const payload = guardians
      .filter((guardian: any) => guardian.guardianCategory !== formData.guardianCategory)
      .concat({
        ...formData,
        id: formData?.id ? formData.id : null,
        guardianCategory: 'secondary',
        other: formData.relation === 'other' ? formData?.other : '',
        mobileNumber: formData?.mobileNumber
          ? formatPhoneNumber(formData?.mobileNumber.slice(1))
          : '',
        email: formData?.email ? formData?.email.toLowerCase() : '',
      });
    props.onUpdateGuardian(payload, () => resetData(formData));
    setReadOnlyWarningDialog(false);
  };

  //return secondary Gaurdian Information component
  return (
    <>
      <Container component='main' maxWidth='sm' sx={{ padding: '0px !important' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{ mt: 1 }}
            component='form'
            onSubmit={handleSubmit((formData) => onSubmitForm(formData))}
          >
            <Box
              sx={{
                marginBottom: 2,
              }}
            >
              <Grid container>
                <Grid item xs={12} sm={12}>
                  <Typography
                    variant='body1'
                    gutterBottom
                    sx={{ backgroundColor: '#FFFBF1', padding: '8px 8px 4px' }}
                  >
                    <b>IMPORTANT: </b>This information is MANDATORY if the patient has a Secondary
                    Guardian. Secondary Guardian is contacted if the primary guardian is
                    unreachable.
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor='firstName'>First Name</InputLabel>

                <TextField
                  id='firstName'
                  autoComplete='firstName'
                  placeholder='Enter Guardian’s First Name'
                  fullWidth
                  {...register('firstName')}
                  error={!!errors.firstName}
                  helperText={errors.firstName ? `${errors.firstName?.message}` : ''}
                  disabled={disableField}
                  inputProps={{
                    maxLength: 255,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor='lastName'>Last Name</InputLabel>
                <TextField
                  id='SGlasttName'
                  autoComplete='lastName'
                  placeholder='Enter Guardian’s Last Name'
                  fullWidth
                  {...register('lastName')}
                  error={!!errors.lastName}
                  helperText={errors.lastName ? errors.lastName?.message : ''}
                  disabled={disableField}
                  inputProps={{
                    maxLength: 255,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor='relation'>Relationship to the patient</InputLabel>
                <TextField
                  id='relation'
                  autoComplete='relation'
                  placeholder='Select relationship to the patient'
                  select // tell TextField to render select
                  fullWidth
                  {...register('relation')}
                  error={!!errors.relation}
                  helperText={errors.relation ? errors.relation?.message : ''}
                  value={watch('relation') ? watch('relation') : 'select_relation_to_the_child'}
                  disabled={disableField}
                >
                  <MenuItem key={-1} value={'select_relation_to_the_child'} disabled>
                    Select relation to the child
                  </MenuItem>
                  {GaurdianRelation.map((option) => (
                    <MenuItem key={option.name} value={option.value}>
                      {option.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor='mobileNumber'>
                  Phone Number
                </InputLabel>
                <ReactPhoneInput
                  component={TextField}
                  label=''
                  onlyCountries={['us']}
                  placeholder='+1 (555) 000-0000'
                  country={'us'}
                  masks={{ us: '(...) ...-....' }}
                  autoFormat={true}
                  disableDropdown={true}
                  defaultErrorMessage={'Please enter a phone number'}
                  {...register('mobileNumber')}
                  inputProps={{
                    helperText: errors.mobileNumber ? errors.mobileNumber?.message : '',
                    error: !!errors.mobileNumber,
                  }}
                  countryCodeEditable={false}
                  onChange={(e) => {
                    setValue('mobileNumber', e, { shouldValidate: true, shouldDirty: true });
                    setIsMobileNumberRequired(false);
                  }}
                  value={watch('mobileNumber') ? watch('mobileNumber') : null}
                  disabled={disableField}
                />
                {isMobileNumberRequired ? (
                  <Typography color='#d32f2f' fontSize='0.75rem'>
                    Please enter Phone Number
                  </Typography>
                ) : (
                  ''
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor='email'>Email Address</InputLabel>
                <TextField
                  id='email'
                  autoComplete='email'
                  placeholder='Enter email address'
                  fullWidth
                  {...register('email')}
                  error={!!errors.email}
                  helperText={errors.email ? errors.email?.message : ''}
                  disabled={disableField}
                  inputProps={{
                    maxLength: 255,
                  }}
                />
              </Grid>
            </Grid>

            <Grid container justifyContent='center'>
              <Grid item>
                <Button
                  size='small'
                  variant='text'
                  sx={{ mt: 2, mb: 2 }}
                  onClick={() => handleSkipStep()}
                >
                  Skip This Step{' '}
                </Button>
                <Button
                  variant='text'
                  sx={{ mt: 2, mb: 2, mr: 2 }}
                  onClick={() => deleteGuardian()}
                  disabled={disableDeleteField}
                >
                  Delete
                </Button>
              </Grid>

              <Grid item>
                <LoadingButton
                  loading={isProfileDetailsFetched}
                  variant='contained'
                  type='submit'
                  fullWidth
                  sx={{
                    minWidth: 140,
                    mt: 2,
                    mb: 2,
                  }}
                  disabled={disableField}
                >
                  Save
                </LoadingButton>
              </Grid>
            </Grid>
          </Box>
        </Box>
        <StyledDialog
          open={readOnlyWarningDialog}
          onClose={() => setReadOnlyWarningDialog(false)}
          maxWidth='xs'
          fullWidth
        >
          <DialogContent sx={{ p: 2 }}>
            <Grid container spacing={1.5} textAlign='center'>
              {/* Title */}
              <Grid item xs={12}>
                <Typography variant='h6' sx={{ fontWeight: 600 }}>
                  Confirm Secondary Guardian Information
                </Typography>
              </Grid>

              {/* Message */}
              <Grid item xs={12}>
                <Typography sx={{ fontSize: '13px', px: 1 }}>
                  Please review before saving. This information cannot be edited later.
                </Typography>
              </Grid>

              {/* Buttons */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    justifyContent: 'center',
                    flexDirection: { xs: 'column', sm: 'row' },
                    mt: 1,
                  }}
                >
                  <Button
                    variant='contained'
                    size='small'
                    sx={{
                      minWidth: 100,
                      px: 2,
                      py: 0.5,
                      fontSize: '0.75rem',
                      borderRadius: '16px',
                    }}
                    onClick={(e) => saveSecondaryGuardian(e)}
                  >
                    Confirm
                  </Button>

                  <Button
                    variant='contained'
                    size='small'
                    sx={{
                      minWidth: 100,
                      px: 2,
                      py: 0.5,
                      fontSize: '0.75rem',
                      borderRadius: '16px',
                    }}
                    onClick={() => setReadOnlyWarningDialog(false)}
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
        </StyledDialog>
      </Container>
    </>
  );
};

export default SecondaryGaurdian;
